import { IChatRepositoryMessage, IChatRepositoryMessageBot, IChatRepositoryMessageUser } from "@core/office/office-chat-repository/office-chat-repository.types";
import { ChatUtilsService } from "@core/shared/chat-utils.service";
import type { IR2Attachment, IR2MessageMeta } from '@common/types/api/r2-types';
import type { IChatMessage, IChatMessageBotOption } from '@core/types/chat-message.types';
import type { ICryptoChannel } from '@services/crypto/crypto-channel.types';

type IChatMessageWithTimestampAndMeta = IChatMessage & { createdAt?: number; };

export class OfficeChatAdapterV1Repository
{
    constructor(private chatUtils: ChatUtilsService, private cryptoService: ICryptoChannel)
    {
    }

    public async encryptMessage(message: IChatMessageWithTimestampAndMeta): Promise<IChatRepositoryMessage>
    {
        if (this.chatUtils.isSenderBot(message))
        {
            const repositoryMessage: IChatRepositoryMessageBot = {
                sender: message.sender,
                value: await this.cryptoService.EncryptMessage(message.value),
                options: await this.cryptoService.EncryptObject(message.options),
                msg_id: message.msg_id,
                createdAt: message.createdAt,
                model: message.model,
                tokensUsage: message.tokensUsage,
            };

            if (message.attachments?.length)
            {
                repositoryMessage.attachments = await this.cryptoService.EncryptObject(message.attachments);
            }

            return repositoryMessage;
        }

        const newItem: IChatRepositoryMessageUser = {
            sender: message.sender,
            value: await this.cryptoService.EncryptMessage(message.value),
            msg_id: message.msg_id,
            createdAt: message.createdAt,
        };

        if (message.attachments?.length)
        {
            newItem.attachments = await this.cryptoService.EncryptObject(message.attachments);
        }
        if ('meta' in message && message.meta)
        {
            newItem.meta = await this.cryptoService.EncryptObject(message.meta);
        }

        return newItem;
    }

    public async encryptMessages(messages: IChatMessageWithTimestampAndMeta[]): Promise<IChatRepositoryMessage[]>
    {
        const encryptedMessages: IChatRepositoryMessage[] = [];

        for (const item of messages)
        {
            encryptedMessages.push(await this.encryptMessage(item));
        }

        return encryptedMessages;
    }

    public async decryptMessages(messages: IChatRepositoryMessage[]): Promise<IChatMessageWithTimestampAndMeta[]>
    {
        const decryptedMessages: IChatMessageWithTimestampAndMeta[] = [];

        for (const item of messages)
        {

            if (this.chatUtils.isSenderRepositoryBot(item))
            {
                const botItem: IChatMessageWithTimestampAndMeta = {
                    sender: item.sender,
                    value: await this.cryptoService.DecryptMessage(item.value),
                    options: await this.cryptoService.DecryptObject<IChatMessageBotOption>(item.options),
                    msg_id: item.msg_id,
                    createdAt: item.createdAt,
                    model: item.model,
                    tokensUsage: item.tokensUsage,
                };

                if (item.attachments)
                {
                    botItem.attachments = await this.cryptoService.DecryptObject<IR2Attachment[]>(item.attachments);
                }

                decryptedMessages.push(botItem);
                continue;
            }

            const userItem = item as IChatRepositoryMessageUser;
            const newItem: IChatMessageWithTimestampAndMeta = {
                sender: userItem.sender,
                value: await this.cryptoService.DecryptMessage(userItem.value),
                msg_id: userItem.msg_id,
                createdAt: userItem.createdAt,
            };

            if (userItem.attachments)
            {
                newItem.attachments = await this.cryptoService.DecryptObject<IR2Attachment[]>(userItem.attachments);
            }
            if (userItem.meta)
            {
                newItem.meta = await this.cryptoService.DecryptObject<IR2MessageMeta>(userItem.meta);
            }

            decryptedMessages.push(newItem);
        }

        return decryptedMessages;
    }
}
